import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /medical-council — Thai hub for medical-council & professional-body certification
import { createFileRoute, Link } from "@tanstack/react-router";
import { MEDICAL_COUNCIL_PROFESSIONS } from "@/data/medical-council-professions";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Stethoscope, ShieldCheck, ScrollText, Globe2, MessageCircle, Phone, BadgeCheck, ArrowRight } from "lucide-react";
import { AIAnswerBox } from "@/components/AIAnswerBox";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_TH } from "@/lib/trust-schema";
const SITE_MC = "https://nyclanguageinstitute.com";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/medical-council`;

export const Route = createFileRoute("/medical-council/")({
  head: () => {
    const title = "รับรองเอกสารแพทยสภา/สภาวิชาชีพ 8 อาชีพ เริ่ม ฿5,500 | NYC Legal";
    const desc = "บริการรับรองเอกสารโดยแพทยสภา สภาการพยาบาล สภาเภสัชกรรม ทันตแพทยสภา และสภาวิชาชีพอื่น พร้อมแปลและรับรองกงสุล/สถานทูตให้ครบจบในที่เดียว ใช้สอบ — ส่งภาพเอกสารมาเช็กให้ก่อน ฟรี ไม่คิดค่าปรึกษา";
    return {
      meta: [
        { title: ctaTitle(title, "/medical-council", "th") }, { name: "description", content: ctaDesc(desc, "/medical-council", "th")},
        { property: "og:title", content: ctaTitle(title, "/medical-council", "th")}, { property: "og:description", content: ctaDesc(desc, "/medical-council", "th")},
        { property: "og:url", content: URL }, { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: URL }, ...hreflangLinks("/medical-council", "/en/medical-council")],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${URL}#service`,
              name: "Medical Council & Professional-Body Certification (Thailand)",
              alternateName: "รับรองเอกสารแพทยสภาและสภาวิชาชีพ",
              serviceType: "Professional Credential Verification",
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999" },
              areaServed: { "@type": "Country", name: "Thailand" },
              aggregateRating: aggregateRatingNode(URL),
              offers: MEDICAL_COUNCIL_PROFESSIONS.map((p) => ({
                "@type": "Offer", name: p.nameEn, price: p.priceFromTh, priceCurrency: "THB",
                url: `${SITE}/medical-council/${p.slug}`,
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${URL}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "รับรองแพทยสภา/สภาวิชาชีพ", item: URL },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "ItemList", "@id": `${URL}#list`,
              numberOfItems: MEDICAL_COUNCIL_PROFESSIONS.length,
              itemListElement: MEDICAL_COUNCIL_PROFESSIONS.map((p, i) => ({
                "@type": "ListItem", position: i + 1, name: p.nameTh,
                url: `${SITE}/medical-council/${p.slug}`,
              })),
            }),
          },
          faqPageSchema([
            { q: "รับรองแพทยสภาคืออะไร ใช้ทำอะไรได้บ้าง?", a: "การรับรองโดยแพทยสภา (Thai Medical Council) คือการที่สภาวิชาชีพแพทย์ออกหนังสือรับรองสถานะใบประกอบวิชาชีพ (Certificate of Good Standing) เพื่อยืนยันว่าบุคคลนั้นเป็นผู้ประกอบวิชาชีพเวชกรรมที่ขึ้นทะเบียนถูกต้อง ใช้สำหรับสอบ ECFMG/USMLE สหรัฐฯ, GMC UK, AHPRA ออสเตรเลีย, MCC แคนาดา และเพื่อขอใบประกอบวิชาชีพในต่างประเทศ" },
            { q: "ค่าบริการรับรองแพทยสภาเริ่มต้นเท่าไหร่?", a: "เริ่มต้น ฿5,500–8,500 (รวมแปลภาษาอังกฤษ + Notary) ไม่รวมค่าธรรมเนียมสภา (200–1,500 บาท) และค่ารับรองกงสุล/สถานทูตที่ขึ้นกับประเทศปลายทาง ทีม NYC ดูแลครบจบในที่เดียว" },
            { q: "ใช้เวลานานแค่ไหน?", a: "งานปกติ 7–15 วันทำการขึ้นกับสภาวิชาชีพ (แพทยสภา 10–15 วัน, สภาการพยาบาล 7–14 วัน) หากต้องรับรองกงสุล+สถานทูตปลายทางเพิ่ม รวมประมาณ 15–25 วันทำการ" },
            { q: "สภาวิชาชีพไหนบ้างที่ NYC ดูแลให้?", a: "แพทยสภา, สภาการพยาบาล, สภาเภสัชกรรม, ทันตแพทยสภา, สภาเทคนิคการแพทย์, สภากายภาพบำบัด, สัตวแพทยสภา และสภาการแพทย์แผนไทย รวม 8 สภาวิชาชีพหลักของประเทศไทย" },
            { q: "ต้องไปที่สภาด้วยตัวเองไหม?", a: "ส่วนใหญ่ไม่ต้อง — NYC รับมอบอำนาจดำเนินการแทน เพียงเตรียมสำเนาบัตรประชาชน + ใบประกอบวิชาชีพ + ใบมอบอำนาจ ส่งให้เราทางไลน์/EMS เราดำเนินการให้ทั้งหมดและจัดส่งเอกสารคืนทั่วประเทศและทั่วโลก" },
            { q: "รับรองสำหรับ ECFMG ส่งตรงได้ไหม?", a: "ได้ครับ แพทยสภาส่ง Verification of Medical Qualification ตรงถึง ECFMG ผ่าน EPIC Portal ได้ NYC ช่วยจัดเตรียมเอกสารและประสานงานกับฝ่ายต่างประเทศของแพทยสภาให้ครบกระบวนการ" },
          ]),
        ]),
        entitySameAsSchema(),
        softwareApplicationSchema({ url: `${SITE_MC}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing Medical Council & professional body certification tooling." }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_TH(`${SITE_MC}/medical-council`)),
      ],
    };
  },
  component: Page,
});

function Page() {
  return (
    <main className="min-h-screen bg-white">
      <AnswerBoxTop cluster="medical-council" locale="th" />
      <div className="mx-auto max-w-6xl px-4 pt-6">
        <AIAnswerBox
          question="รับรองเอกสารแพทยสภาเพื่อใช้ทำงานต่างประเทศ ต้องทำอะไรบ้าง?"
          answer="NYC ดูแลแพทย์/พยาบาล/เภสัชกร/ทันตแพทย์ครบขั้นตอน — ขอ Certificate of Good Standing จากสภาวิชาชีพ → แปลอังกฤษโดยนักแปล MoJ → Notary → MFA แจ้งวัฒนะ → สถานทูตปลายทาง เริ่ม ฿5,500 ใช้เวลา 7–15 วันทำการ ครอบคลุม 8 สภาวิชาชีพ"
          stats={[
            { label: "สภาวิชาชีพ", value: "8 สภา" },
            { label: "ราคาเริ่ม", value: "฿5,500" },
            { label: "ระยะเวลา", value: "7–15 วัน" },
            { label: "เคสสะสม", value: "2,400+" },
          ]}
          sourceUrl={`${SITE_MC}/medical-council`}
          lang="th"
        />
      </div>
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">รับรองแพทยสภา/สภาวิชาชีพ</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Stethoscope className="h-3.5 w-3.5" /> Medical Council & Professional Body Certification
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            รับรองเอกสารแพทยสภา · สภาการพยาบาล · สภาเภสัชกรรม และสภาวิชาชีพอื่น
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            บริการครบวงจรสำหรับบุคลากรทางการแพทย์ไทยที่ต้องการนำใบประกอบวิชาชีพไปใช้ในต่างประเทศ —
            ขอ Certificate of Good Standing, แปลภาษาอังกฤษ, Notary Public, รับรองกงสุล MFA และสถานทูตปลายทาง จบในที่เดียว
            พร้อมประสบการณ์มากกว่า 12 ปี ดูแลแพทย์ พยาบาล เภสัชกร ทันตแพทย์ และนักเทคนิคการแพทย์ไปแล้วกว่า 2,400 เคส
          </p>
          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "สภาวิชาชีพ", v: "8 สภา" }, { k: "ราคาเริ่ม", v: "฿5,500" },
              { k: "TAT ปกติ", v: "7–15 วัน" }, { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> ปรึกษา LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable Stat
          </span>
          NYC Legal & Notary Service (DBD 0435567000061) — บริการรับรองเอกสารแพทยสภาและสภาวิชาชีพ 8 สภา
          (แพทย์/พยาบาล/เภสัช/ทันตแพทย์/เทคนิคการแพทย์/กายภาพ/สัตวแพทย์/แพทย์แผนไทย)
          เริ่มต้น ฿5,500 เสร็จภายใน 7–15 วันทำการ บริการสำเร็จ 2,147+ เคส ครอบคลุมประเทศปลายทาง 20+ ประเทศ
          รวม USMLE/ECFMG (สหรัฐ), GMC (สหราชอาณาจักร), AHPRA (ออสเตรเลีย), MCC (แคนาดา)
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เลือกสภาวิชาชีพ</h2>
          <p className="mt-2 text-sm text-slate-600">คลิกเพื่อดูรายละเอียดเอกสาร ขั้นตอน ราคา และประเทศปลายทางที่รองรับ</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {MEDICAL_COUNCIL_PROFESSIONS.map((p) => (
              <Link key={p.slug} to="/medical-council/$profession" params={{ profession: p.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-amber-700">
                    <ScrollText className="h-3 w-3" /> {p.councilTh}
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-amber-600" />
                </div>
                <h3 className="mt-2 font-display text-lg font-extrabold text-navy-deep group-hover:text-amber-700">{p.nameTh}</h3>
                <p className="mt-1 text-[11px] text-slate-500">{p.nameEn}</p>
                <div className="mt-3 flex items-center gap-3 text-[12px] text-slate-700">
                  <span className="font-semibold">เริ่ม ฿{p.priceFromTh.toLocaleString()}</span>
                  <span className="text-slate-400">·</span>
                  <span>{p.turnaround}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {p.destinations.slice(0, 4).map((d) => (
                    <span key={d.code} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700">{d.nameTh}</span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ขั้นตอนมาตรฐาน</div>
          <h2 className="mt-1 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">5 ขั้นตอนสู่ใบประกอบวิชาชีพต่างประเทศ</h2>
          <ol className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {[
              { n: 1, t: "ขอเอกสารจากสภา", d: "ทำหนังสือถึงสภาวิชาชีพไทย ขอ Certificate of Good Standing + Verification" },
              { n: 2, t: "แปลภาษาอังกฤษ", d: "แปลโดยนักแปล MoJ หรือ NAATI พร้อมรับรองคำแปล" },
              { n: 3, t: "Notary Public", d: "ทนายผู้ทำคำรับรองลายมือชื่อและเอกสารรับรองคำแปล" },
              { n: 4, t: "MFA / กงสุล", d: "รับรองที่กรมการกงสุล กระทรวงการต่างประเทศ" },
              { n: 5, t: "สถานทูตปลายทาง", d: "รับรองที่สถานทูตประเทศปลายทาง (ถ้าจำเป็น)" },
            ].map((s) => (
              <li key={s.n} className="rounded-xl border border-amber-200 bg-white p-4">
                <div className="text-2xl font-extrabold text-amber-600">{s.n}</div>
                <div className="mt-1 font-display font-extrabold text-navy-deep">{s.t}</div>
                <div className="mt-1 text-[12px] leading-relaxed text-slate-600">{s.d}</div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">บริการที่เกี่ยวข้อง</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { to: "/notary", t: "Notary Public", d: "รับรองลายมือชื่อทนาย 30 ประเภท" },
              { to: "/mfa-legalisation", t: "MFA / กงสุล", d: "รับรองกระทรวงต่างประเทศ" },
              { to: "/embassy", t: "สถานทูต", d: "รับรองสถานทูตทุกประเทศ" },
              { to: "/translate", t: "แปลและรับรอง", d: "แปลโดย MoJ / NAATI ทุกภาษา" },
            ].map((s) => (
              <Link key={s.to} to={s.to as any} className="rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-700"><Globe2 className="h-3 w-3" /> Related</div>
                <div className="mt-1.5 font-display font-extrabold text-navy-deep">{s.t}</div>
                <div className="mt-1 text-[12px] text-slate-600">{s.d}</div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-3xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8">
          <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-amber-700"><ShieldCheck className="h-3 w-3" /> Authority Sources</div>
          <h2 className="mt-1 font-display text-xl font-extrabold text-navy-deep sm:text-2xl">แหล่งอ้างอิงทางการ</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2 text-[13px] text-slate-700">
            {MEDICAL_COUNCIL_PROFESSIONS.map((p) => (
              <li key={p.slug}>
                <a href={p.councilUrl} target="_blank" rel="noopener noreferrer nofollow" className="text-amber-700 underline-offset-2 hover:underline">
                  {p.councilTh} ({p.councilEn})
                </a>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <TrustLayer variant="compact" />
    
      <PlanningToolsLinks locale="th" />
      </main>
  );
}
